import { register as registerBodyOverlay } from '../utils/body-overlays';
import type { ViewLifecycle } from '../utils/view-lifecycle';

/**
 * Photo-source picker — fixed-position menu showing «Камера» / «Галерея»
 * options anchored near the trigger button. Replaces the inline pattern that
 * used to live in chat-view.ts (with a module-level `closeActivePhotoSourcePicker`
 * variable) — now it's a proper body-overlay with replace-semantics.
 *
 * The picker is dismissed on: option click, outside click, Esc keypress, or
 * owner view's lc.cleanup. Returns nothing — the caller cannot manually close
 * the picker, but `lc.cleanup()` will tear it down.
 *
 * v0.80.9 (2026-05-04) extracted into bodyOverlays in Plan B Task 11
 * (2026-05-11). Pre-extraction the picker state lived inside a
 * render-closure inside chat-view.ts and required a module-level
 * `closeActivePhotoSourcePicker` slot so destroyChatView could sweep it on
 * tap-then-navigate-without-choosing. Now the registry owns the singleton
 * lifetime via `lc.onDestroy` — same global-singleton class as the
 * connection-bar bug fixed in v0.80.7.
 */
export function openPhotoSourcePicker(
  anchor: HTMLElement,
  lc: ViewLifecycle,
  onPick: (source: 'camera' | 'gallery') => void,
): void {
  const panel = document.createElement('div');
  panel.className = 'chat-photo-source-picker';
  panel.setAttribute('role', 'menu');
  panel.style.cssText = `
    position: fixed; z-index: 200;
    background: var(--bg-card); border: 1px solid var(--bg-border);
    border-radius: 14px; padding: 6px; display: flex; flex-direction: column; gap: 2px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.35);
    min-width: 160px;
  `;

  const dismiss = (): void => {
    panel.remove();
    document.removeEventListener('click', outsideHandler, true);
    document.removeEventListener('keydown', escHandler);
  };

  const makeOption = (label: string, emoji: string, onPickInternal: () => void): HTMLButtonElement => {
    const opt = document.createElement('button');
    opt.type = 'button';
    opt.style.cssText = `
      background: transparent; border: none; cursor: pointer;
      font-size: 15px; padding: 10px 14px; border-radius: 10px;
      color: var(--text-primary); text-align: left;
      display: flex; align-items: center; gap: 10px;
      min-height: 44px;
    `;
    opt.innerHTML = `<span style="font-size:18px;line-height:1;">${emoji}</span><span>${label}</span>`;
    opt.addEventListener('mouseenter', () => { opt.style.background = 'rgba(255,184,0,0.10)'; });
    opt.addEventListener('mouseleave', () => { opt.style.background = 'transparent'; });
    opt.addEventListener('click', (e) => {
      e.stopPropagation();
      dismiss();
      onPickInternal();
    });
    return opt;
  };

  panel.appendChild(makeOption('Камера', '📷', () => onPick('camera')));
  panel.appendChild(makeOption('Галерея', '🖼️', () => onPick('gallery')));
  document.body.appendChild(panel);

  // Anchor above the photo button; flip below if no room.
  const anchorRect = anchor.getBoundingClientRect();
  const panelRect = panel.getBoundingClientRect();
  let top = anchorRect.top - panelRect.height - 8;
  if (top < 8) top = anchorRect.bottom + 8;
  let left = anchorRect.left;
  left = Math.max(8, Math.min(left, window.innerWidth - panelRect.width - 8));
  panel.style.top = `${top}px`;
  panel.style.left = `${left}px`;

  const outsideHandler = (e: Event): void => {
    if (!panel.contains(e.target as Node)) dismiss();
  };
  const escHandler = (e: Event): void => {
    if ((e as KeyboardEvent).key === 'Escape') dismiss();
  };
  // capture=true so outside taps land before the click bubbles back up
  // to the photo button (which would re-open us).
  // Defer attaching until after the click that opened the picker has propagated.
  setTimeout(() => document.addEventListener('click', outsideHandler, true), 0);
  document.addEventListener('keydown', escHandler);

  registerBodyOverlay('photo-source', dismiss, lc);
}
